import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Cookie, Copy, Check, Trash2, ChevronLeft, ChevronRight, Mail, BarChart, TrendingUp, Clock, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_KEY = process.env.REACT_APP_API_KEY;
const ITEMS_PER_PAGE = 5;

const CookiesManager = () => {
  const [cookies, setCookies] = useState([]);
  const [groupedCookies, setGroupedCookies] = useState({});
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteEmail, setDeleteEmail] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [cookiesAddedToday, setCookiesAddedToday] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // Usar ref para evitar re-renders
  const isFirstLoad = useRef(true);
  const previousDataHash = useRef(null);

  // Calcular cookies adicionados hoje
  const calculateTodaysCookies = (cookiesList) => {
    const today = new Date().toDateString();
    return cookiesList.filter(cookie => {
      const cookieDate = new Date(cookie.timestamp).toDateString();
      return cookieDate === today;
    }).length;
  };

  // Encontrar última atualização
  const findLastUpdate = (cookiesList) => {
    if (cookiesList.length === 0) return null;
    const timestamps = cookiesList.map(c => new Date(c.timestamp).getTime());
    const mostRecent = Math.max(...timestamps);
    return new Date(mostRecent);
  };

  const fetchCookies = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cookies`, {
        headers: {
          "X-API-Key": API_KEY
        }
      });
      
      const fetchedCookies = response.data.cookies || [];
      const fetchedGrouped = response.data.grouped_by_email || {};
      
      // Criar hash dos dados para comparação
      const dataHash = JSON.stringify({
        cookies: fetchedCookies.map(c => ({id: c.id, usage_count: c.usage_count})),
        grouped: Object.keys(fetchedGrouped).sort()
      });
      
      // Só atualizar se os dados mudaram
      if (dataHash !== previousDataHash.current) {
        previousDataHash.current = dataHash;
        
        setCookies(fetchedCookies);
        setGroupedCookies(fetchedGrouped);
        setCookiesAddedToday(calculateTodaysCookies(fetchedCookies));
        setLastUpdate(findLastUpdate(fetchedCookies));
      }
      
      // Remover loading apenas na primeira vez
      if (isFirstLoad.current) {
        isFirstLoad.current = false;
        setLoading(false);
      }
    } catch (error) {
      console.error("Erro ao buscar cookies:", error);
      if (isFirstLoad.current) {
        isFirstLoad.current = false;
        setLoading(false);
      }
    }
  };

  const handleDeleteEmail = async () => {
    if (!deleteEmail) return;
    
    try {
      const response = await axios.delete(`${BACKEND_URL}/api/cookies/${encodeURIComponent(deleteEmail)}`, {
        headers: {
          "X-API-Key": API_KEY
        }
      });
      
      // Atualizar apenas o estado local, sem recarregar tudo
      const newGroupedCookies = { ...groupedCookies };
      delete newGroupedCookies[deleteEmail];
      setGroupedCookies(newGroupedCookies);
      
      // Atualizar lista de cookies removendo os do email deletado
      const newCookies = cookies.filter(cookie => cookie.email !== deleteEmail);
      setCookies(newCookies);
      
      // Ajustar página se necessário
      const emailEntries = Object.entries(newGroupedCookies);
      const newTotalPages = Math.ceil(emailEntries.length / ITEMS_PER_PAGE);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }
      
      setShowDeleteDialog(false);
      setDeleteEmail(null);
    } catch (error) {
      console.error("Erro ao deletar cookies:", error);
      setShowDeleteDialog(false);
      setDeleteEmail(null);
    }
  };

  const openDeleteDialog = (email) => {
    setDeleteEmail(email);
    setShowDeleteDialog(true);
  };

  useEffect(() => {
    fetchCookies();
    
    // Atualizar a cada 5 segundos para mostrar distribuição em tempo real
    const interval = setInterval(() => {
      fetchCookies();
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatRelativeTime = (date) => {
    if (!date) return "Nunca";
    
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffSecs < 60) return "Agora mesmo";
    if (diffMins < 60) return `${diffMins} min atrás`;
    if (diffHours < 24) return `${diffHours}h atrás`;
    if (diffDays < 7) return `${diffDays}d atrás`;
    return date.toLocaleDateString("pt-BR");
  };

  const truncateCookie = (cookieText, maxLength = 100) => {
    if (!cookieText || cookieText.length <= maxLength) {
      return cookieText;
    }
    
    const startLength = Math.floor(maxLength * 0.6);
    const endLength = Math.floor(maxLength * 0.3);
    
    return `${cookieText.substring(0, startLength)}...${cookieText.substring(cookieText.length - endLength)}`;
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Preparar dados da página atual
  const emailsList = Object.keys(groupedCookies).sort();
  const totalPages = Math.ceil(emailsList.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentEmails = emailsList.slice(startIndex, endIndex);

  return (
    <div className="space-y-6" key="cookies-container">{/* Key fixa para evitar re-mount */}
      {/* Stats Cards - Grid Responsivo com Contadores */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
        {/* Total de Cookies */}
        <div className="group relative bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-gray-800 rounded-lg p-4 lg:p-5 hover:border-gray-700 transition-all duration-300 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-600 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
          
          <div className="relative z-10">
            <div className="flex items-start justify-between mb-3">
              <div className="h-10 w-10 lg:h-11 lg:w-11 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Cookie className="h-5 w-5 lg:h-5.5 lg:w-5.5 text-white" />
              </div>
            </div>
            
            <div className="space-y-1">
              <h3 className="text-2xl lg:text-3xl font-bold text-white tracking-tight">
                {cookies.length}
              </h3>
              <p className="text-xs lg:text-sm text-gray-400 font-medium">
                Total de Cookies
              </p>
            </div>
          </div>
          
          <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-blue-500 to-blue-600 blur-xl -z-10"></div>
        </div>

        {/* Emails Únicos */}
        <div className="group relative bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-gray-800 rounded-lg p-4 lg:p-5 hover:border-gray-700 transition-all duration-300 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-purple-600 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
          
          <div className="relative z-10">
            <div className="flex items-start justify-between mb-3">
              <div className="h-10 w-10 lg:h-11 lg:w-11 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Mail className="h-5 w-5 lg:h-5.5 lg:w-5.5 text-white" />
              </div>
            </div>
            
            <div className="space-y-1">
              <h3 className="text-2xl lg:text-3xl font-bold text-white tracking-tight">
                {Object.keys(groupedCookies).length}
              </h3>
              <p className="text-xs lg:text-sm text-gray-400 font-medium">
                Emails Únicos
              </p>
            </div>
          </div>
          
          <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-purple-500 to-purple-600 blur-xl -z-10"></div>
        </div>

        {/* Média por Email */}
        <div className="group relative bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-gray-800 rounded-lg p-4 lg:p-5 hover:border-gray-700 transition-all duration-300 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-green-600 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
          
          <div className="relative z-10">
            <div className="flex items-start justify-between mb-3">
              <div className="h-10 w-10 lg:h-11 lg:w-11 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <BarChart className="h-5 w-5 lg:h-5.5 lg:w-5.5 text-white" />
              </div>
            </div>
            
            <div className="space-y-1">
              <h3 className="text-2xl lg:text-3xl font-bold text-white tracking-tight">
                {Object.keys(groupedCookies).length > 0
                  ? (cookies.length / Object.keys(groupedCookies).length).toFixed(1)
                  : 0}
              </h3>
              <p className="text-xs lg:text-sm text-gray-400 font-medium">
                Média por Email
              </p>
            </div>
          </div>
          
          <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-green-500 to-green-600 blur-xl -z-10"></div>
        </div>

        {/* Cookies Adicionados Hoje */}
        <div className="group relative bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-gray-800 rounded-lg p-4 lg:p-5 hover:border-gray-700 transition-all duration-300 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-orange-600 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
          
          <div className="relative z-10">
            <div className="flex items-start justify-between mb-3">
              <div className="h-10 w-10 lg:h-11 lg:w-11 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <TrendingUp className="h-5 w-5 lg:h-5.5 lg:w-5.5 text-white" />
              </div>
            </div>
            
            <div className="space-y-1">
              <h3 className="text-2xl lg:text-3xl font-bold text-white tracking-tight">
                {cookiesAddedToday}
              </h3>
              <p className="text-xs lg:text-sm text-gray-400 font-medium">
                Adicionados Hoje
              </p>
            </div>
          </div>
          
          <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-orange-500 to-orange-600 blur-xl -z-10"></div>
        </div>

        {/* Última Atualização */}
        <div className="group relative bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-gray-800 rounded-lg p-4 lg:p-5 hover:border-gray-700 transition-all duration-300 overflow-hidden col-span-2 lg:col-span-1">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-cyan-600 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
          
          <div className="relative z-10">
            <div className="flex items-start justify-between mb-3">
              <div className="h-10 w-10 lg:h-11 lg:w-11 rounded-lg bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Clock className="h-5 w-5 lg:h-5.5 lg:w-5.5 text-white" />
              </div>
            </div>
            
            <div className="space-y-1">
              <h3 className="text-xl lg:text-2xl font-bold text-white tracking-tight">
                {formatRelativeTime(lastUpdate)}
              </h3>
              <p className="text-xs lg:text-sm text-gray-400 font-medium">
                Última Atualização
              </p>
            </div>
          </div>
          
          <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-cyan-500 to-cyan-600 blur-xl -z-10"></div>
        </div>
      </div>

      {/* Cookies by Email */}
      {Object.keys(groupedCookies).length === 0 ? (
        <div className="bg-[#1a1a1a] border border-gray-800 rounded-lg p-8 text-center">
          <Cookie className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">Nenhum cookie salvo ainda</p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {currentEmails.map((email) => {
              const emailCookies = groupedCookies[email] || [];
              return (
              <div
                key={email}
                className="bg-[#1a1a1a] border border-gray-800 rounded-lg overflow-hidden"
              >
                {/* Email Header - Compacto */}
                <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border-b border-gray-800 p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Cookie className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-bold text-white">{email}</h3>
                          {(() => {
                            const totalUsage = emailCookies.reduce((sum, cookie) => sum + (cookie.usage_count || 0), 0);
                            return totalUsage > 0 && (
                              <span className="bg-green-500/20 text-green-400 text-xs font-bold px-2 py-0.5 rounded border border-green-500/30">
                                {totalUsage} req
                              </span>
                            );
                          })()}
                        </div>
                        <p className="text-xs text-gray-400">
                          {emailCookies.length} {emailCookies.length === 1 ? "cookie" : "cookies"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="bg-blue-500/20 text-blue-400 text-xs font-medium px-2 py-1 rounded-full">
                        {emailCookies.length}/4
                      </span>
                      <button
                        onClick={() => openDeleteDialog(email)}
                        className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors"
                        title="Deletar todos os cookies deste email"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Cookies List - Compacto */}
                <div className="divide-y divide-gray-800">
                  {emailCookies.map((cookie, index) => (
                    <div key={cookie.id} className="p-3 hover:bg-gray-900/50 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="bg-purple-500/20 text-purple-400 text-xs font-medium px-2 py-0.5 rounded">
                            #{index + 1}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(cookie.timestamp)}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        {/* Cookie - Compacto e Truncado */}
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-[#0a0a0a] rounded px-2 py-1.5 border border-gray-800 group relative">
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <span className="text-xs font-medium text-gray-400 block mb-0.5">Cookie</span>
                                <p className="text-xs text-gray-300 font-mono break-all line-clamp-1">
                                  {truncateCookie(cookie.cookie, 80)}
                                </p>
                                {cookie.cookie.length > 80 && (
                                  <span className="text-[10px] text-gray-500 mt-0.5 inline-block">
                                    {cookie.cookie.length} caracteres (clique em copiar para obter completo)
                                  </span>
                                )}
                                {/* Contador de Requisições do Cookie */}
                                {cookie.usage_count > 0 && (
                                  <div className="mt-1 flex items-center gap-1">
                                    <Activity className="h-3 w-3 text-green-400" />
                                    <span className="text-[10px] text-green-400 font-semibold">
                                      {cookie.usage_count} {cookie.usage_count === 1 ? 'requisição' : 'requisições'}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => copyToClipboard(cookie.cookie, `cookie-${cookie.id}`)}
                            className="p-1.5 text-gray-400 hover:text-blue-400 transition-colors bg-[#0a0a0a] rounded border border-gray-800 flex-shrink-0"
                            title="Copiar cookie completo"
                          >
                            {copiedField === `cookie-${cookie.id}` ? (
                              <Check className="h-3.5 w-3.5 text-green-400" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
            })}
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-4 bg-[#1a1a1a] border border-gray-800 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-400">
                Página {currentPage} de {totalPages} • {emailsList.length} email(s)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="bg-gray-800 border-gray-700 text-white disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="bg-gray-800 border-gray-700 text-white disabled:opacity-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Diálogo de Confirmação de Exclusão */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-[#1a1a1a] border-gray-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Tem certeza que deseja deletar todos os cookies do email <span className="font-semibold text-white">{deleteEmail}</span>?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 text-white border-gray-700 hover:bg-gray-700">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEmail}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CookiesManager;
